/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useTranslation } from "react-i18next";

// components imports
import { Button, Card, Col, Form, Input, Result, Row } from "antd";
import { Link, useLocation } from "react-router-dom";

import { NAV_LOGIN } from "@/constants";
import { useRequestResetPassword } from "@/api/user";
import { ErrorsComponent } from "@/components/UI/ErrorsComponent";
import { IRequestError } from "@/interfaces/ServerError.interface";

type FieldType = {
  email?: string;
};

/**
 * Forgot Password Screen
 * @returns JSX.Element
 */
export const ForgotPasswordScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();

  const { mutate: resetPasswordMutate, isLoading: isResettingPassword, error, isSuccess } = useRequestResetPassword();

  const onFinish = (values: any) => {
    resetPasswordMutate(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  // Show form to reset password
  if (!isSuccess) {
    return (
      <Row align={"middle"} justify={"center"} className="w-100">
        <Col lg={10} xl={9} md={14} xs={24}>
          <Card title={t("forgot_password.title")}>
            <Form
              name="forgot-password-form"
              layout="vertical"
              labelAlign="left"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              initialValues={{
                email: location.state?.email || "",
              }}
            >
              {/* Email */}
              <Form.Item<FieldType>
                label={t("common.email")}
                name="email"
                rules={[
                  {
                    required: true,
                    type: "email",
                  },
                ]}
              >
                <Input />
              </Form.Item>
              {/* ./ Email */}

              {/* Submit button */}
              <Form.Item>
                <Button className="mr-3" disabled={isResettingPassword} loading={isResettingPassword} type="primary" htmlType="submit">
                  {t("common.send")}
                </Button>
                <Link to={NAV_LOGIN}>Back to sign in</Link>
                <div className="mt-3">
                  <ErrorsComponent error={error as unknown as IRequestError} />
                </div>
              </Form.Item>
              {/* Submit button */}
            </Form>
          </Card>
        </Col>
      </Row>
    );
  }
  return (
    <Row align={"middle"} justify={"center"} className="w-100">
      <Col lg={12} md={16} xs={24}>
        <Card title={t("forgot_password.title")}>
          {isSuccess ? (
            <>
              <Result status="success" title={t("forgot_password.success.title")} />
              <Link to={NAV_LOGIN}>{t("forgot_password.sign_in_link")}</Link>
            </>
          ) : (
            <>
              <Result status="error" title={t("forgot_password.error.title")} />
            </>
          )}
        </Card>
      </Col>
    </Row>
  );
};
